/**
 * useTestDataDeletion Hook
 * Handles test data deletion with confirmation modal
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 3.1
 */

import { useState, useCallback } from 'react';
import type { DeleteTestDataResponse } from '~/types/test-data';
import { getAuthToken } from './useTestDataStatus';

interface ToastHelpers {
  success: (message: string) => void;
  error: (message: string) => void;
}

interface UseTestDataDeletionOptions {
  /** Whether App Bridge is ready */
  isReady: boolean;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Toast helpers for notifications */
  toast?: ToastHelpers;
  /** Revalidate function to refresh loader data */
  revalidate?: () => void;
  /** Refresh status after deletion */
  fetchStatus: () => Promise<unknown>;
}

interface UseTestDataDeletionReturn {
  /** Whether deletion is in progress */
  isDeleting: boolean;
  /** Whether removal modal is open */
  removalModalOpen: boolean;
  /** Delete test data */
  deleteTestData: () => Promise<boolean>;
  /** Open removal confirmation modal */
  openRemovalModal: () => void;
  /** Close removal confirmation modal */
  closeRemovalModal: () => void;
  /** Confirm and execute removal */
  confirmRemoval: () => Promise<void>;
}

/**
 * Hook for managing test data deletion with confirmation modal
 */
export function useTestDataDeletion({
  isReady,
  onError,
  toast,
  revalidate,
  fetchStatus,
}: UseTestDataDeletionOptions): UseTestDataDeletionReturn {
  const [isDeleting, setIsDeleting] = useState(false);
  const [removalModalOpen, setRemovalModalOpen] = useState(false);

  // Delete all test data (requires App Bridge to be ready)
  const deleteTestData = useCallback(async (): Promise<boolean> => {
    if (!isReady) {
      onError?.('App Bridge not ready. Please wait and try again.');
      return false;
    }

    setIsDeleting(true);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch('/api/test-data', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json() as DeleteTestDataResponse;

      if (!data.success) {
        throw new Error(data.message || 'Failed to delete test data');
      }

      await fetchStatus();
      return true;
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Unknown error');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [isReady, fetchStatus, onError]);

  const openRemovalModal = useCallback(() => {
    setRemovalModalOpen(true);
  }, []);

  const closeRemovalModal = useCallback(() => {
    setRemovalModalOpen(false);
  }, []);

  const confirmRemoval = useCallback(async () => {
    try {
      const result = await deleteTestData();
      if (result) {
        toast?.success('Test data deleted successfully');
        revalidate?.();
      }
    } catch {
      toast?.error('Failed to delete test data');
    } finally {
      closeRemovalModal();
    }
  }, [deleteTestData, toast, revalidate, closeRemovalModal]);

  return {
    isDeleting,
    removalModalOpen,
    deleteTestData,
    openRemovalModal,
    closeRemovalModal,
    confirmRemoval,
  };
}
